import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from './use-auth'

export interface EmailTemplate {
  id: number
  name: string | null
  subject: string | null
  body: string | null
  is_primary: boolean | null
  company_id: string
}

export interface NotificationSchedule {
  /** Paused keeps the wording and timing; deleting loses both. */
  enabled?: boolean
  id: number
  name: string | null
  relative_to_date: string | null      // planned_start_date | planned_end_date | actual_start_date | actual_end_date
  relative_to_date_type: string | null  // minutes | hours | days | months | years
  relative_to_date_value: number | null // negative = before, 0 = on, positive = after
  custom_form_id: string | null
  notification_email_template_id: number | null
  condition: string | null              // JSON string e.g. {"status":{"_eq":"Pending"}}
}

export interface FormOption { id: string; name: string | null }

// Template merge tokens (stored as [[token]] — matches the Flutter app).
export const TEMPLATE_TOKENS: { group: string; tokens: string[] }[] = [
  { group: 'Company', tokens: ['company.name'] },
  { group: 'Contact', tokens: ['contact.first_name', 'contact.last_name', 'contact.address', 'contact.city'] },
  { group: 'Job', tokens: ['task.name', 'task.address', 'task.city', 'task.planned_start_date', 'task.planned_end_date', 'task.planned_start_time', 'task.planned_end_time'] },
  { group: 'User', tokens: ['user.first_name', 'user.last_name', 'user.email', 'user.phone', 'user.role'] },
]

export const SCHEDULE_STATUSES = ['All', 'Pending', 'In-Progress', 'On-Hold', 'Completed', 'Completed-Follow-Up', 'Closed', 'Cancelled', 'Failed', 'Awaiting Approval']
export const conditionFor = (status: string) => (status === 'All' ? '{"status": {"_neq": "All"}}' : `{"status": {"_eq": "${status}"}}`)
export const statusFromCondition = (c: string | null): string => {
  if (!c) return 'All'
  const m = c.match(/"_eq":\s*"([^"]+)"/)
  return m ? m[1] : 'All'
}

/**
 * What this job's notifications are doing — sent, due, or gone wrong.
 *
 * Before the queue existed a pending notification had no representation at all: the only record was
 * a nightly query that had not run yet, so neither the trade person nor support could answer "is the
 * customer getting a reminder?" This is that answer.
 *
 * Two sources, because they mean different things. `scheduled_action` is the queue and covers
 * everything from here on. `notification_email_history` is the older record of what actually went
 * out, and jobs that were emailed before the queue existed appear only there.
 */
export interface JobNotification {
  /** -1 for a review request, which has no notification_schedule row. */
  ruleId: number
  name: string
  status: 'pending' | 'sent' | 'failed' | 'skipped' | 'cancelled'
  at: string | null      // sent_at when sent, due_at when pending
  /** Plain English, safe to show. The raw error lives in a column the app never reads. */
  reason: string | null
}

export function useJobNotifications(taskId: number) {
  return useQuery({
    queryKey: ['job-notifications', taskId],
    queryFn: async (): Promise<JobNotification[]> => {
      const [queue, history] = await Promise.all([
        supabase.from('scheduled_action')
          .select('rule_id, kind, status, due_at, sent_at, reason, notification_schedule(name)')
          .eq('task_id', taskId).order('due_at'),
        supabase.from('notification_email_history')
          .select('notification_schedule_id, created_at, success, notification_schedule(name)')
          .eq('task_id', taskId).order('created_at'),
      ])

      // PostgREST returns a single object for a many-to-one embed, but the generated types model
      // every embed as an array. Accept both rather than asserting one and being wrong later.
      type Rel = { name: string | null } | { name: string | null }[] | null
      const relName = (v: Rel): string => {
        const o = Array.isArray(v) ? v[0] : v
        return o?.name?.trim() || 'Notification'
      }

      type QRow = {
        rule_id: number | null; kind: string; status: JobNotification['status']
        due_at: string; sent_at: string | null
        reason: string | null; notification_schedule: Rel
      }
      const out: JobNotification[] = ((queue.data ?? []) as unknown as QRow[]).map((r) => ({
        // Review requests have no schedule row to take a name from.
        ruleId: r.rule_id ?? -1,
        name: r.kind === 'review-request' ? 'Review request' : relName(r.notification_schedule),
        status: r.status,
        at: r.status === 'sent' ? r.sent_at : r.due_at,
        reason: r.reason,
      }))

      // A send that predates the queue. Without this the card would claim a job was never emailed
      // when the customer has the email in their inbox.
      const seen = new Set(out.map((r) => r.ruleId))
      type HRow = {
        notification_schedule_id: number | null; created_at: string; success: boolean | null
        notification_schedule: Rel
      }
      for (const h of (history.data ?? []) as unknown as HRow[]) {
        if (h.notification_schedule_id == null || seen.has(h.notification_schedule_id)) continue
        seen.add(h.notification_schedule_id)
        out.push({
          ruleId: h.notification_schedule_id,
          name: relName(h.notification_schedule),
          status: h.success === false ? 'failed' : 'sent',
          at: h.created_at,
          reason: null,
        })
      }
      return out
    },
  })
}

/**
 * Failing rows per schedule, so a systemic fault surfaces in Settings where she already looks
 * rather than only on the individual jobs it broke.
 */
export function useScheduleTrouble() {
  return useQuery({
    queryKey: ['schedule-trouble'],
    queryFn: async (): Promise<Record<number, number>> => {
      const { data } = await supabase.from('scheduled_action')
        .select('rule_id').eq('status', 'failed').limit(1000)
      const out: Record<number, number> = {}
      for (const r of (data ?? []) as { rule_id: number }[]) out[r.rule_id] = (out[r.rule_id] ?? 0) + 1
      return out
    },
  })
}

/**
 * How much automated messaging this business has spent this month.
 *
 * SMS has always had an allowance and prepaid credits. Email had neither, so when a review-request
 * bug matched a year of completed jobs, 198 emails left in 21 seconds with nothing in the product
 * able to say that was abnormal — it had to be reconstructed from the mail provider afterwards.
 */
export interface SendUsage {
  emails_this_month: number
  email_limit: number
  sms_free_this_month: number
  sms_credits: number
}

/** Matches SMS_FREE_ALLOWANCE in the SMS sender. */
export const SMS_FREE_ALLOWANCE = 20

export function useSendUsage() {
  return useQuery({
    queryKey: ['send-usage'],
    queryFn: async (): Promise<SendUsage | null> => {
      const { data } = await supabase.rpc('company_send_usage')
      const row = Array.isArray(data) ? data[0] : data
      return (row as SendUsage) ?? null
    },
  })
}

/** Last send per schedule, so the list can show whether a rule is actually working. */
export function useScheduleSends() {
  return useQuery({
    queryKey: ['schedule-sends'],
    queryFn: async (): Promise<Record<number, { at: string; count: number }>> => {
      const { data } = await supabase
        .from('notification_email_history')
        .select('notification_schedule_id, created_at')
        .order('created_at', { ascending: false })
        .limit(2000)
      const out: Record<number, { at: string; count: number }> = {}
      for (const r of (data ?? []) as { notification_schedule_id: number | null; created_at: string }[]) {
        if (r.notification_schedule_id == null) continue
        const e = out[r.notification_schedule_id]
        if (e) e.count++
        else out[r.notification_schedule_id] = { at: r.created_at, count: 1 }
      }
      return out
    },
  })
}

export function useNotifications() {
  const { companyId } = useAuth()
  const qc = useQueryClient()

  const templates = useQuery({
    queryKey: ['email-templates'],
    queryFn: async (): Promise<EmailTemplate[]> => {
      const { data, error } = await supabase.from('notification_email_template').select('id, name, subject, body, is_primary, company_id').is('deleted_at', null).order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as EmailTemplate[]
    },
  })

  const schedules = useQuery({
    queryKey: ['notification-schedules'],
    queryFn: async (): Promise<NotificationSchedule[]> => {
      const { data, error } = await supabase.from('notification_schedule').select('id, name, relative_to_date, relative_to_date_type, relative_to_date_value, custom_form_id, notification_email_template_id, condition, enabled').is('deleted_at', null).order('id', { ascending: false })
      if (error) throw error
      return (data ?? []) as NotificationSchedule[]
    },
  })

  const forms = useQuery({
    queryKey: ['custom-forms-options'],
    queryFn: async (): Promise<FormOption[]> => {
      const { data, error } = await supabase.from('custom_form').select('id, name').is('deleted_at', null).order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as FormOption[]
    },
  })

  const saveTemplate = useMutation({
    mutationFn: async (p: Partial<EmailTemplate> & { id?: number }) => {
      const payload = { name: p.name ?? null, subject: p.subject ?? null, body: p.body ?? null, is_primary: p.is_primary ?? false, updated_at: new Date().toISOString() }
      if (p.id) {
        const { error } = await supabase.from('notification_email_template').update(payload).eq('id', p.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('notification_email_template').insert({ ...payload, company_id: companyId })
        if (error) throw error
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  })

  const removeTemplate = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase.from('notification_email_template').update({ deleted_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['email-templates'] }),
  })

  const saveSchedule = useMutation({
    mutationFn: async (p: Partial<NotificationSchedule> & { id?: number }) => {
      // NULL relative_to_date is a MEANING, not a missing value: the queue reads it as "send when
      // the job is created" (sync_scheduled_actions sets due_at = now() for it), and the editor
      // offers it as the first option. Defaulting it to planned_start_date silently rewrote every
      // send-on-creation schedule into "on the day of the job", so a customer who booked nine days
      // out heard nothing for nine days (a customer, 2026-08-12). `??` only fills undefined, but the
      // editor sends an explicit null, so the coalesce was firing on exactly the case that matters.
      const onCreate = p.relative_to_date === null
      const payload = {
        name: p.name ?? null,
        relative_to_date: onCreate ? null : (p.relative_to_date ?? 'planned_start_date'),
        relative_to_date_type: onCreate ? null : (p.relative_to_date_type ?? 'days'),
        relative_to_date_value: onCreate ? null : (p.relative_to_date_value ?? 0),
        custom_form_id: p.custom_form_id ?? null,
        notification_email_template_id: p.notification_email_template_id ?? null,
        condition: p.condition ?? conditionFor('All'),
        enabled: p.enabled ?? true,
        updated_at: new Date().toISOString(),
      }
      if (p.id) {
        const { error } = await supabase.from('notification_schedule').update(payload).eq('id', p.id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('notification_schedule').insert(payload)
        if (error) throw error
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-schedules'] }),
  })

  const removeSchedule = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase.from('notification_schedule').update({ deleted_at: new Date().toISOString() }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-schedules'] }),
  })

  return {
    templates: templates.data ?? [],
    schedules: schedules.data ?? [],
    forms: forms.data ?? [],
    isLoading: templates.isLoading || schedules.isLoading,
    saveTemplate, removeTemplate, saveSchedule, removeSchedule,
  }
}
